"use client";

import { cx } from "@tripcart/traveler-ui/cx";

import styles from "./_group-tiers.module.css";

import { useTranslator } from "@/lib/translation-context";

/**
 * One group-discount band, ready to render.
 *
 * `price` arrives ALREADY FORMATTED, and that is deliberate. The two surfaces
 * that show these bands sit on different money paths — the package card holds
 * major units and formats with `formatMajorMoney`, the booking rail holds minor
 * units and formats with `useFormatMoney` (see this route's CLAUDE.md, money
 * invariant 1). Carrying a raw number here would force one of them to convert,
 * which is exactly where a hardcoded `/100` gets written.
 */
export interface GroupTierBand {
  from: number;
  /** `null` is the open-ended top band — "7 or more". */
  to: number | null;
  price: string;
}

/** Which band this party falls in, or -1 when it falls below the first one.
 *
 *  A party too small for any configured band highlights nothing, rather than
 *  wrongly lighting up the cheapest row. */
export function activeBandIndex(
  bands: readonly GroupTierBand[],
  headcount: number,
): number {
  if (headcount <= 0) return -1;
  return bands.findIndex(
    (b) => headcount >= b.from && (b.to === null || headcount <= b.to),
  );
}

/**
 * Guide `.tier-tb` — the booking rail's two-column table.
 *
 * A table, not a list: this panel has room for column headers, and "Travelers"
 * over "Per person" is what makes the second number readable as a rate rather
 * than as a total for the party.
 */
export function GroupTierTable({
  bands,
  headcount,
  unitNoun,
}: {
  bands: readonly GroupTierBand[];
  /**
   * How many of `unitNoun` the party has — matched against the bands.
   *
   * It MUST be the count the bands are expressed in, which is the count of the
   * one category they came from. It used to be the party's total head-count
   * across every category, which is wrong twice over: on a per-unit category a
   * 1-4 band means four COUPLES (eight people), and even on a per-person one
   * a party of 2 Adults + 1 Child lit the "3 to 5" row while pricing charged
   * the Adult rate for 2. Both advertise a discount the traveler is not
   * getting. `getPriceForCount` matches on the per-category count, and this
   * table has to agree with it.
   */
  headcount: number;
  /** Plural of what a band COUNTS — "travelers", "couples", "cabins". */
  unitNoun: string;
}) {
  const t = useTranslator();
  const active = activeBandIndex(bands, headcount);

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            {/* `ink-muted`, not the guide's `--subtle`: a column header is read,
              and `ink-subtle` only clears the 3:1 NON-text floor (WCAG
              1.4.11). See `packages/ui/CLAUDE.md` §2.2. */}
            {/* Both headers name the SAME unit the bands count. They read
              "Travelers" / "Per person" unconditionally before, so a Couple
              category's ladder was labelled per-person while its prices are
              per couple — the 2x misreading the package card was fixed for. */}
            <th className={styles.head}>{unitNoun}</th>
            <th className={cx(styles.head, styles.numeric)}>{t("Each")}</th>
          </tr>
        </thead>
        <tbody>
          {bands.map((band, i) => (
            <tr
              key={`${band.from}-${band.to ?? "plus"}`}
              className={styles.row}
              data-active={i === active ? "" : undefined}
            >
              <td className={styles.cell}>
                {band.to === null
                  ? t("{from} or more", { from: band.from })
                  : t("{from} to {to}", { from: band.from, to: band.to })}
              </td>
              <td className={cx(styles.cell, styles.numeric, styles.price)}>
                {band.price}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Guide `.tp-r` — the package card's narrower list.
 *
 * The same bands without column headers, because this panel hangs off a chip
 * that already names what it is and has 38px less to work with.
 *
 * `unitNoun` is the plural of what a band actually COUNTS, and it is not always
 * "travelers": a per-unit category's bands count units, so a 1-4 band on a
 * Couple category means four couples — eight people. Hardcoding the noun here
 * is the 2x misreading the card's own tier table was fixed for.
 */
export function GroupTierRows({
  bands,
  headcount,
  unitNoun,
}: {
  bands: readonly GroupTierBand[];
  headcount: number;
  unitNoun: string;
}) {
  const t = useTranslator();
  const active = activeBandIndex(bands, headcount);

  return (
    <span className={styles.rows}>
      {bands.map((band, i) => {
        const isNow = i === active;
        return (
          <span
            key={`${band.from}-${band.to ?? "plus"}`}
            className={styles.band}
            data-active={isNow ? "" : undefined}
          >
            <span>
              {band.to === null
                ? t("{from}+ {unit}", { from: band.from, unit: unitNoun })
                : t("{from}-{to} {unit}", {
                    from: band.from,
                    to: band.to,
                    unit: unitNoun,
                  })}
            </span>
            <b className={styles.bandPrice}>
              {t("{price} each", { price: band.price })}
            </b>
          </span>
        );
      })}
    </span>
  );
}
